import { Diagnosis } from '../@types/common.types';
import {
    AppointmentActionType,
    AppointmentDxStatus,
    AppointmentStatus,
    ClientStatus,
    CoverageType,
    FaxJobHistoryStatus,
    GroupMemberStatus,
    GroupSessionStatus,
    InsuranceClaimStatus,
    OrganizationMemberStatus,
    OrganizationStatus,
    PaymentStatus,
    PayoutStatus,
} from '../enums/shared.enums';

// TODO: Probably move some of these to constants

export const FULL_STATUSES = {
    [AppointmentStatus.CANCELED]: 'components.statuses.appointment.canceled',
    [AppointmentStatus.MISSED_CLIENT]: 'components.statuses.appointment.missedByClient',
    [AppointmentStatus.MISSED_PROVIDER]: 'components.statuses.appointment.missedByProvider',
    [AppointmentStatus.OCCURRED]: 'components.statuses.appointment.occurred',
    [AppointmentStatus.PENDING]: 'components.statuses.appointment.pending',
    [AppointmentStatus.CONFIRMED]: 'components.statuses.appointment.confirmed',
    [GroupSessionStatus.CONFIRMED]: 'components.statuses.appointment.confirmed',
    [GroupSessionStatus.RESCHEDULED]: 'components.statuses.appointment.rescheduled',
    [GroupSessionStatus.COMPLETED]: 'components.statuses.appointment.occurred',
    [GroupSessionStatus.CANCELED]: 'components.statuses.appointment.canceled',
    [GroupSessionStatus.MISSED_BY_PROVIDER]: 'components.statuses.appointment.missedByProvider',
    [GroupSessionStatus.DELETED]: 'components.statuses.appointment.deleted',
    None: 'components.statuses.appointment.none',
};

export const FULL_ACTION_TYPES = {
    [AppointmentActionType.MARK_AS_OCCURED]: 'components.statuses.appointmentAction.markAsOccurred',
    [AppointmentActionType.CANCEL]: 'components.statuses.appointmentAction.cancel',
    [AppointmentActionType.CONFIRM]: 'components.statuses.appointmentAction.confirm',
    [AppointmentActionType.RESCHEDULE]: 'components.statuses.appointmentAction.reschedule',
    [AppointmentActionType.OVERRIDE]: 'components.statuses.appointmentAction.override',
    [AppointmentActionType.CHARGE_FEE]: 'components.statuses.appointmentAction.chargeFee',
    [AppointmentActionType.CHARGE_CUSTOM_PAYMENT]:
        'components.statuses.appointmentAction.chargeCustomPayment',
};

export const APPOINTMENT_STATUS_TOOLTIPS = {
    [AppointmentStatus.CANCELED]: 'components.statuses.appointmentTooltip.canceled',
    [AppointmentStatus.MISSED_CLIENT]: 'components.statuses.appointmentTooltip.missedByClient',
    [AppointmentStatus.MISSED_PROVIDER]: 'components.statuses.appointmentTooltip.missedByProvider',
    [AppointmentStatus.OCCURRED]: 'components.statuses.appointmentTooltip.occurred',
    [AppointmentStatus.PENDING]: 'components.statuses.appointmentTooltip.pending',
    [AppointmentStatus.CONFIRMED]: 'components.statuses.appointmentTooltip.confirmed',
};

export const CLAIM_STATUS_TOOLTIPS = {
    [InsuranceClaimStatus.UNSUBMITTED]: 'components.claim.status.unsubmitted',
    [InsuranceClaimStatus.SUBMITTED]: 'components.claim.status.submitted',
    [InsuranceClaimStatus.PAID]: 'components.claim.status.paid',
    [InsuranceClaimStatus.DENIED]: 'components.claim.status.denied',
};

export const PAYOUT_STATUS_TOOLTIPS = {
    [PayoutStatus.TRANSFERRED]: 'components.statuses.payoutTooltip.transferred',
    [PayoutStatus.TRANSFER_FAILED]: 'components.statuses.payoutTooltip.transferFailed',
    [PayoutStatus.PAID]: 'components.statuses.payoutTooltip.paid',
    [PayoutStatus.PAYOUT_FAILED]: 'components.statuses.payoutTooltip.payoutFailed',
};

export const DX_STATUS_TOOLTIPS = {
    [AppointmentDxStatus.INCOMPLETE]: 'components.statuses.dxTooltip.incomplete',
    [AppointmentDxStatus.CODES_SUBMITTED]: 'components.statuses.dxTooltip.codesSubmitted',
    [AppointmentDxStatus.INCOMPLETE_NOTES]: 'components.statuses.dxTooltip.incompleteNotes',
    [AppointmentDxStatus.COMPLETE]: 'components.statuses.dxTooltip.complete',
    [AppointmentDxStatus.NA]: 'components.statuses.dxTooltip.na',
};

export const CLIENT_STATUS_TOOLTIPS = {
    [ClientStatus.PENDING]: 'components.statuses.clientTooltip.pending',
    [ClientStatus.PAUSED]: 'components.statuses.clientTooltip.paused',
    [ClientStatus.CUSTOMER]: 'components.statuses.clientTooltip.customer',
    [ClientStatus.CANCELED]: 'components.statuses.clientTooltip.canceled',
    [ClientStatus.LEAD]: 'components.statuses.clientTooltip.lead',
};

export const ORG_MEMBER_STATUS_TOOLTIPS = {
    [OrganizationMemberStatus.ACTIVE]: 'components.statuses.orgMemberTooltip.active',
    [OrganizationMemberStatus.TERMINATED]: 'components.statuses.orgMemberTooltip.terminated',
    [OrganizationMemberStatus.REMOVED]: 'components.statuses.orgMemberTooltip.removed',
};

export const ORG_STATUS_TOOLTIPS = {
    [OrganizationStatus.PENDING]: 'components.statuses.orgTooltip.pending',
    [OrganizationStatus.ACTIVE]: 'components.statuses.orgTooltip.active',
    [OrganizationStatus.SUSPENDED]: 'components.statuses.orgTooltip.suspended',
    [OrganizationStatus.TERMINATED]: 'components.statuses.orgTooltip.terminated',
};

export const FAX_STATUS_TOOLTIPS = {
    [FaxJobHistoryStatus.COMPLETE]: 'The fax was successfully sent and delivered.',
    [FaxJobHistoryStatus.FAILED]: 'The fax failed to send.',
    [FaxJobHistoryStatus.IN_PROGRESS]: 'The fax is currently being processed.',
    [FaxJobHistoryStatus.QUEUED]: 'The fax is queued for sending.',
    [FaxJobHistoryStatus.CANCELLED]: 'The fax was cancelled.',
};

export function getGroupSessionStatusLabelColor(status: GroupSessionStatus): string {
    switch (status) {
        case GroupSessionStatus.CONFIRMED:
            return 'primary';
        case GroupSessionStatus.RESCHEDULED:
            return 'warning';
        case GroupSessionStatus.COMPLETED:
            return 'success';
        case GroupSessionStatus.CANCELED:
        case GroupSessionStatus.MISSED_BY_PROVIDER:
        case GroupSessionStatus.DELETED:
            return 'error';
        default:
            return 'default';
    }
}

type GroupMemberStatusColor = 'primary' | 'warning' | 'error' | 'default';

export function getGroupMemberStatusLabelColor(status: GroupMemberStatus): GroupMemberStatusColor {
    switch (status) {
        case GroupMemberStatus.JOINED:
        case GroupMemberStatus.REACTIVATED:
        case GroupMemberStatus.UNBLOCKED:
            return 'primary';
        case GroupMemberStatus.WAITLISTED:
        case GroupMemberStatus.REPORT:
            return 'warning';
        case GroupMemberStatus.REMOVED:
        case GroupMemberStatus.BLOCKED:
        case GroupMemberStatus.SUSPENDED:
            return 'error';
        case GroupMemberStatus.LEFT:
        default:
            return 'default';
    }
}

export function getAppointmentStatusLabelColor(status: AppointmentStatus): string {
    switch (status) {
        case AppointmentStatus.CANCELED:
            return 'error';
        case AppointmentStatus.MISSED_CLIENT:
            return 'error';
        case AppointmentStatus.MISSED_PROVIDER:
            return 'error';
        case AppointmentStatus.OCCURRED:
            return 'success';
        case AppointmentStatus.PENDING:
            return 'warning';
        case AppointmentStatus.CONFIRMED:
            return 'primary';
        default:
            return 'default';
    }
}

export function getClaimStatusLabelColor(status: InsuranceClaimStatus): string {
    switch (status) {
        case InsuranceClaimStatus.UNSUBMITTED:
            return 'warning';
        case InsuranceClaimStatus.SUBMITTED:
            return 'info';
        case InsuranceClaimStatus.PAID:
            return 'success';
        case InsuranceClaimStatus.DENIED:
            return 'error';
        default:
            return 'default';
    }
}

export function getPayoutStatusLabelColor(status: PayoutStatus): string {
    switch (status) {
        case PayoutStatus.TRANSFERRED:
            return 'warning';
        case PayoutStatus.TRANSFER_FAILED:
            return 'error';
        case PayoutStatus.PAID:
            return 'success';
        default:
            return 'default';
    }
}

export function getClientStatusLabelColor(status: ClientStatus): string {
    switch (status) {
        case ClientStatus.PENDING:
            return 'warning';
        case ClientStatus.PAUSED:
            return 'default';
        case ClientStatus.CUSTOMER:
            return 'success';
        case ClientStatus.CANCELED:
            return 'error';
        default:
            return 'default';
    }
}

export function getOrgMemberStatusLabelColor(status: OrganizationMemberStatus): string {
    switch (status) {
        case OrganizationMemberStatus.ACTIVE:
            return 'success';
        case OrganizationMemberStatus.TERMINATED:
            return 'error';
        case OrganizationMemberStatus.REMOVED:
            return 'default';
        default:
            return 'default';
    }
}

export function getOrganizationStatusLabel(status: OrganizationStatus): string {
    switch (status) {
        case OrganizationStatus.ACTIVE:
            return 'success';
        case OrganizationStatus.TERMINATED:
            return 'error';
        case OrganizationStatus.SUSPENDED:
            return 'warning';
        default:
            return 'default';
    }
}

export function getPaymentStatusLabelColor(status: PaymentStatus): string {
    switch (status) {
        case PaymentStatus.SUCCEEDED:
            return 'success';
        case PaymentStatus.REFUNDED:
            return 'warning';
        case PaymentStatus.UNPAID:
            return 'error';
        default:
            return 'default';
    }
}

export function getDxStatusFromDx(
    diagnosis: Diagnosis | null | undefined,
    coverageType?: CoverageType | null
): AppointmentDxStatus {
    if (coverageType === CoverageType.EAP) return AppointmentDxStatus.NA;
    if (!diagnosis) return AppointmentDxStatus.NA;
    if (!diagnosis.areCodesLocked) return AppointmentDxStatus.INCOMPLETE;
    if (diagnosis.areCodesLocked && !diagnosis.areNotesLocked)
        return AppointmentDxStatus.INCOMPLETE_NOTES;
    if (diagnosis.areNotesLocked) return AppointmentDxStatus.COMPLETE;
    return AppointmentDxStatus.CODES_SUBMITTED;
}

export function getDxStatusLabelColor(status: AppointmentDxStatus): string {
    switch (status) {
        case AppointmentDxStatus.INCOMPLETE:
            return 'error';
        case AppointmentDxStatus.CODES_SUBMITTED:
            return 'info';
        case AppointmentDxStatus.INCOMPLETE_NOTES:
            return 'warning';
        case AppointmentDxStatus.COMPLETE:
            return 'success';
        case AppointmentDxStatus.NA:
            return 'default';
        default:
            return 'default';
    }
}

export function getFaxStatusLabelColor(status: FaxJobHistoryStatus): string {
    switch (status) {
        case FaxJobHistoryStatus.COMPLETE:
            return 'success';
        case FaxJobHistoryStatus.FAILED:
            return 'error';
        case FaxJobHistoryStatus.IN_PROGRESS:
            return 'warning';
        case FaxJobHistoryStatus.QUEUED:
            return 'info';
        case FaxJobHistoryStatus.CANCELLED:
            return 'error';
        default:
            const exhaustiveCheck: never = status;
            console.log('Unknown fax status: ', exhaustiveCheck);
            return 'info';
    }
}
